import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import {
  Shield, Phone, Trash2, Plus, X, PhoneCall,
  Mic, Users, Activity, Clock, BarChart3, RefreshCw
} from 'lucide-react';

export default function AdminPanel() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNumberForm, setShowNumberForm] = useState(false);
  const [numberForm, setNumberForm] = useState({ phone_number: '', friendly_name: '' });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [dashRes, numRes, recRes, agentRes] = await Promise.all([
        api.get('/api/admin/dashboard'),
        api.get('/api/admin/twilio-numbers'),
        api.get('/api/admin/recordings'),
        api.get('/api/agents')
      ]);
      setDashboard(dashRes.data);
      setTwilioNumbers(numRes.data.numbers);
      setRecordings(recRes.data.recordings);
      setAgents(agentRes.data.agents);
    } catch (err) {
      console.error('Error loading admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNumber = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/twilio-numbers', numberForm);
      setShowNumberForm(false);
      setNumberForm({ phone_number: '', friendly_name: '' });
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error adding number');
    }
  };

  const handleDeleteNumber = async (id) => {
    if (!window.confirm('Remove this Twilio number?')) return;
    try {
      await api.delete(`/api/admin/twilio-numbers/${id}`);
      loadAll();
    } catch (err) {
      alert('Error removing number');
    }
  };

  const handleAssignNumber = async (agentId, numberId) => {
    try {
      await api.put(`/api/admin/agents/${agentId}/twilio-number`, {
        twilio_number_id: numberId || null
      });
      loadAll();
    } catch (err) {
      alert('Error assigning number');
    }
  };

  const handleDeleteRecording = async (recordingSid) => {
    if (!window.confirm('Delete this recording permanently?')) return;
    try {
      await api.delete(`/api/admin/recordings/${recordingSid}`);
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting recording');
    }
  };

  const handleAgentTypeChange = async (agentId, newRole) => {
    try {
      await api.put(`/api/admin/agents/${agentId}/type`, { role: newRole });
      loadAll();
    } catch (err) {
      alert('Error updating agent type');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="glass rounded-xl p-8 text-center">
        <Shield className="w-12 h-12 text-dark-500 mx-auto mb-3" />
        <p className="text-dark-400">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'agents', label: 'Agent Mgmt', icon: Users },
    { id: 'numbers', label: 'Twilio Numbers', icon: Phone },
    { id: 'recordings', label: 'Recordings', icon: Mic },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white">Admin Panel</h3>
          <p className="text-dark-400 text-sm">Manage agents, numbers, and recordings</p>
        </div>
        <button onClick={loadAll} className="p-2 text-dark-400 hover:text-white rounded-lg hover:bg-dark-800">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-900 p-1 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-500/10 text-primary-400'
                : 'text-dark-400 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <MiniStat label="VoIP Agents" value={dashboard.summary.voip_agents} />
            <MiniStat label="Normal Agents" value={dashboard.summary.normal_agents} />
            <MiniStat label="Total Leads" value={dashboard.summary.total_leads} />
            <MiniStat label="VoIP Calls" value={dashboard.summary.total_voip_calls} />
            <MiniStat label="Manual Calls" value={dashboard.summary.total_manual_calls} />
          </div>

          {/* Agent Performance Table */}
          <div className="glass rounded-xl overflow-hidden">
            <div className="p-4 border-b border-dark-700">
              <h4 className="text-white font-medium">Agent Performance</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-700">
                    <th className="text-left py-3 px-4 text-dark-400 font-medium">Agent</th>
                    <th className="text-left py-3 px-4 text-dark-400 font-medium">Type</th>
                    <th className="text-left py-3 px-4 text-dark-400 font-medium">Twilio #</th>
                    <th className="text-right py-3 px-4 text-dark-400 font-medium">Calls</th>
                    <th className="text-right py-3 px-4 text-dark-400 font-medium">Talk Time</th>
                    <th className="text-right py-3 px-4 text-dark-400 font-medium">Active Today</th>
                    <th className="text-right py-3 px-4 text-dark-400 font-medium">Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.agent_stats.map(agent => (
                    <tr key={agent.id} className="border-b border-dark-800 hover:bg-dark-800/30">
                      <td className="py-3 px-4 text-white">{agent.name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          agent.agent_type === 'voip_agent' ? 'bg-green-500/10 text-green-400' : 'bg-dark-600 text-dark-400'
                        }`}>{agent.agent_type === 'voip_agent' ? '📞 VoIP' : '📱 Normal'}</span>
                      </td>
                      <td className="py-3 px-4 text-dark-300 text-xs">{agent.twilio_number || '-'}</td>
                      <td className="py-3 px-4 text-right text-white">{agent.voip_calls + agent.manual_calls}</td>
                      <td className="py-3 px-4 text-right text-white">{formatDuration(agent.total_talk_time_seconds)}</td>
                      <td className="py-3 px-4 text-right text-white">{agent.active_minutes_today}m</td>
                      <td className="py-3 px-4 text-right text-white">{agent.assigned_leads}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Agents Management Tab */}
      {activeTab === 'agents' && (
        <div className="space-y-3">
          {agents.map(agent => (
            <div key={agent.id} className="glass rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-white font-medium">{agent.name}</h4>
                  <p className="text-dark-400 text-xs">@{agent.username} &bull; {agent.phone || 'No phone'}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={agent.role}
                    onChange={(e) => handleAgentTypeChange(agent.id, e.target.value)}
                    className="px-3 py-1.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-xs"
                  >
                    <option value="voip_agent">VoIP Agent</option>
                    <option value="normal_agent">Normal Agent</option>
                  </select>
                  {agent.role === 'voip_agent' && (
                    <select
                      value={twilioNumbers.find(n => n.phone_number === agent.twilio_number)?.id || ''}
                      onChange={(e) => handleAssignNumber(agent.id, e.target.value)}
                      className="px-3 py-1.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-xs"
                    >
                      <option value="">No Twilio Number</option>
                      {twilioNumbers.map(num => (
                        <option key={num.id} value={num.id}>
                          {num.phone_number} {num.friendly_name ? `(${num.friendly_name})` : ''}
                          {num.assigned_agent_id && num.assigned_agent_id !== agent.id ? ' (assigned)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Twilio Numbers Tab */}
      {activeTab === 'numbers' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              onClick={() => setShowNumberForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm"
            >
              <Plus className="w-4 h-4" /> Add Number
            </button>
          </div>
          {twilioNumbers.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center">
              <Phone className="w-10 h-10 text-dark-500 mx-auto mb-2" />
              <p className="text-dark-400">No Twilio numbers in pool</p>
              <p className="text-dark-500 text-xs mt-1">Add numbers purchased from Twilio console</p>
            </div>
          ) : (
            twilioNumbers.map(num => (
              <div key={num.id} className="glass rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{num.phone_number}</p>
                    <p className="text-dark-400 text-xs">
                      {num.friendly_name || 'No name'} &bull;
                      {num.assigned_agent_id
                        ? ` Assigned to: ${agents.find(a => a.id === num.assigned_agent_id)?.name || 'Unknown'}`
                        : ' Unassigned (Pool)'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteNumber(num.id)}
                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Recordings Tab */}
      {activeTab === 'recordings' && (
        <div className="space-y-3">
          {recordings.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center">
              <Mic className="w-10 h-10 text-dark-500 mx-auto mb-2" />
              <p className="text-dark-400">No recordings yet</p>
            </div>
          ) : (
            recordings.map(rec => (
              <div key={rec.id} className="glass rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <p className="text-white text-sm">
                      {rec.direction === 'outbound' ? `To: ${rec.to_number}` : `From: ${rec.from_number}`}
                    </p>
                    <p className="text-dark-400 text-xs">
                      Duration: {formatDuration(rec.duration)} &bull; {new Date(rec.timestamp).toLocaleString()}
                      &bull; Agent: {rec.agent_id?.substring(0, 8)}...
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {rec.recording_url && (
                      <a href={rec.recording_sid ? `/api/calls/recording/${rec.recording_sid}` : rec.recording_url} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 rounded-lg text-xs">
                        <Mic className="w-3 h-3 inline mr-1" /> Play
                      </a>
                    )}
                    {rec.recording_sid && (
                      <button
                        onClick={() => handleDeleteRecording(rec.recording_sid)}
                        className="px-3 py-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg text-xs"
                      >
                        <Trash2 className="w-3 h-3 inline mr-1" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add Number Form */}
      {showNumberForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-dark-700">
              <h3 className="text-lg font-semibold text-white">Add Twilio Number</h3>
              <button onClick={() => setShowNumberForm(false)} className="p-1 text-dark-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddNumber} className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-dark-300 mb-1.5">Phone Number (E.164) *</label>
                <input type="text" value={numberForm.phone_number}
                  onChange={(e) => setNumberForm({...numberForm, phone_number: e.target.value})}
                  placeholder="+15017122661"
                  className="w-full px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                  required />
              </div>
              <div>
                <label className="block text-sm text-dark-300 mb-1.5">Friendly Name</label>
                <input type="text" value={numberForm.friendly_name}
                  onChange={(e) => setNumberForm({...numberForm, friendly_name: e.target.value})}
                  placeholder="Main Line, Sales Line..."
                  className="w-full px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowNumberForm(false)}
                  className="flex-1 py-2.5 bg-dark-800 text-dark-300 hover:text-white rounded-lg text-sm font-medium">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">
                  Add Number
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="glass rounded-xl p-4 text-center">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-dark-400 mt-1">{label}</div>
    </div>
  );
}
